import {
  Box,
  MenuItem,
  Slider,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  FillPatternKey,
  LayerStyle,
  LineStyleKey,
  StoryLayerKey,
} from "../../types/storyMap";
import {
  FILL_PATTERN_LABELS,
  LINE_STYLE_LABELS,
  patternCss,
  STYLE_CAPABILITIES,
  SWATCH_PALETTE,
} from "./layerStyles";

/**
 * Style controls shared by the layer-entry dialog (per-entry LayerStyle) and
 * the freehand drawing tool (per-feature style, via the same field names).
 * Only the controls the layer key honours are shown.
 */

const PATTERNS: FillPatternKey[] = ["none", "hatch", "hatch_back", "crosshatch", "dots"];
const LINE_STYLES: LineStyleKey[] = ["solid", "dashed", "dotted"];

export function ColorField({
  label,
  value,
  fallback,
  onChange,
  allowClear = true,
}: {
  label: string;
  value: string | undefined;
  /** Shown when unset (the layer's built-in colour). */
  fallback: string;
  onChange: (color: string | undefined) => void;
  allowClear?: boolean;
}) {
  const current = value ?? fallback;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        {label}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
        <Box
          component="input"
          type="color"
          value={current}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
          aria-label={label}
          sx={{
            width: 28,
            height: 28,
            p: 0,
            border: "1px solid rgba(148,163,184,0.5)",
            borderRadius: "4px",
            background: "transparent",
            cursor: "pointer",
            "&::-webkit-color-swatch-wrapper": { p: 0 },
            "&::-webkit-color-swatch": { border: "none", borderRadius: "3px" },
          }}
        />
        {SWATCH_PALETTE.map((swatch) => (
          <Box
            key={swatch}
            role="button"
            aria-label={swatch}
            onClick={() => onChange(swatch)}
            sx={{
              width: 18,
              height: 18,
              borderRadius: "50%",
              bgcolor: swatch,
              cursor: "pointer",
              border:
                current.toLowerCase() === swatch.toLowerCase()
                  ? "2px solid #00d4ad"
                  : "1px solid rgba(148,163,184,0.5)",
            }}
          />
        ))}
        {allowClear && value != null && (
          <Typography
            variant="caption"
            role="button"
            onClick={() => onChange(undefined)}
            sx={{ color: "text.secondary", cursor: "pointer", textDecoration: "underline" }}
          >
            default
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export function LineStyleToggle({
  value,
  onChange,
  color = "#212b37",
}: {
  value: LineStyleKey | undefined;
  onChange: (style: LineStyleKey) => void;
  color?: string;
}) {
  const preview = (style: LineStyleKey) => (
    <Box
      sx={{
        width: 28,
        height: 0,
        borderTop: `3px ${style} ${color}`,
      }}
    />
  );
  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      color="secondary"
      value={value ?? "solid"}
      onChange={(_, next: LineStyleKey | null) => next && onChange(next)}
      sx={{ display: "flex" }}
    >
      {LINE_STYLES.map((style) => (
        <Tooltip key={style} title={LINE_STYLE_LABELS[style]}>
          <ToggleButton value={style} sx={{ flex: 1, py: 0.75 }} aria-label={LINE_STYLE_LABELS[style]}>
            {preview(style)}
          </ToggleButton>
        </Tooltip>
      ))}
    </ToggleButtonGroup>
  );
}

function PercentSlider({
  label,
  value,
  onChange,
  min = 0,
  max = 1,
  step = 0.05,
  format = (v: number) => `${Math.round(v * 100)}%`,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  format?: (v: number) => string;
}) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <Typography variant="caption" sx={{ color: "text.secondary", minWidth: 72 }}>
        {label}
      </Typography>
      <Slider
        size="small"
        color="secondary"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(_, v) => onChange(Array.isArray(v) ? v[0] : v)}
        valueLabelDisplay="auto"
        valueLabelFormat={format}
        sx={{ flex: 1 }}
      />
      <Typography variant="caption" sx={{ minWidth: 36, textAlign: "right" }}>
        {format(value)}
      </Typography>
    </Box>
  );
}

interface LayerStyleFieldsProps {
  layerKey: StoryLayerKey;
  style: LayerStyle | undefined;
  onChange: (style: LayerStyle | undefined) => void;
  /** Built-in colours, for the pickers' "unset" state. */
  defaults: { color: string; lineColor?: string };
}

/** Per-entry style editor; renders only the controls the key honours. */
export default function LayerStyleFields({
  layerKey,
  style,
  onChange,
  defaults,
}: LayerStyleFieldsProps) {
  const caps = STYLE_CAPABILITIES[layerKey];
  if (caps.length === 0) return null;
  const has = (cap: (typeof caps)[number]) => caps.includes(cap);
  const patch = (p: Partial<LayerStyle>) => {
    const next: LayerStyle = { ...style, ...p };
    // Drop unset keys so an untouched style stays absent from the content.
    (Object.keys(next) as (keyof LayerStyle)[]).forEach((k) => {
      if (next[k] == null) delete next[k];
    });
    onChange(Object.keys(next).length ? next : undefined);
  };
  const lineDefault = defaults.lineColor ?? defaults.color;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
      <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
        Style
      </Typography>
      {has("fill") && (
        <>
          <ColorField
            label="Fill colour"
            value={style?.color}
            fallback={defaults.color}
            onChange={(color) => patch({ color })}
          />
          <PercentSlider
            label="Fill opacity"
            value={style?.fillOpacity ?? 0.4}
            onChange={(fillOpacity) => patch({ fillOpacity })}
          />
          <TextField
            select
            size="small"
            label="Fill pattern"
            value={style?.fillPattern ?? "none"}
            onChange={(e) =>
              patch({
                fillPattern:
                  e.target.value === "none" ? undefined : (e.target.value as FillPatternKey),
              })
            }
          >
            {PATTERNS.map((pattern) => (
              <MenuItem key={pattern} value={pattern}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: 22,
                      height: 14,
                      borderRadius: "2px",
                      background: patternCss(pattern, style?.color ?? defaults.color),
                      border: "1px solid rgba(0,0,0,0.25)",
                    }}
                  />
                  {FILL_PATTERN_LABELS[pattern]}
                </Box>
              </MenuItem>
            ))}
          </TextField>
        </>
      )}
      {has("fillOpacity") && !has("fill") && (
        <PercentSlider
          label="Opacity"
          value={style?.fillOpacity ?? 0.75}
          onChange={(fillOpacity) => patch({ fillOpacity })}
        />
      )}
      {has("raster") && (
        <PercentSlider
          label="Opacity"
          value={style?.opacity ?? 0.6}
          onChange={(opacity) => patch({ opacity })}
        />
      )}
      {has("line") && (
        <>
          <ColorField
            label={has("fill") ? "Outline colour" : "Line colour"}
            value={style?.lineColor}
            fallback={style?.color ?? lineDefault}
            onChange={(lineColor) => patch({ lineColor })}
          />
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Line style
            </Typography>
            <LineStyleToggle
              value={style?.lineStyle}
              onChange={(lineStyle) =>
                patch({ lineStyle: lineStyle === "solid" ? undefined : lineStyle })
              }
              color={style?.lineColor ?? style?.color ?? lineDefault}
            />
          </Box>
          <PercentSlider
            label="Line width"
            value={style?.lineWidth ?? 2}
            min={0.5}
            max={12}
            step={0.5}
            format={(v) => `${v} px`}
            onChange={(lineWidth) => patch({ lineWidth })}
          />
        </>
      )}
      {has("point") && (
        <PercentSlider
          label="Point size"
          value={style?.pointRadius ?? 6}
          min={2}
          max={20}
          step={1}
          format={(v) => `${v} px`}
          onChange={(pointRadius) => patch({ pointRadius })}
        />
      )}
    </Box>
  );
}
